import { ButtonBase, Skeleton, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { ErrorBoundary } from "react-error-boundary"
import { useNavigate } from "react-router"

import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useMoney } from "../../../../../currency/useMoney"
import { toDinero } from "../../../../../currency/currencyLib"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { RefetchIntervals } from "../../../../../../core/api/refetchIntervals"
import { USER_REPORTS_SETTINGS_WALLETS_ROUTE } from "../../../../../../constants/routes"
import { UserDashboardErrorFallbackCard } from "../UserDashboardErrorFallbackCard"
import { CARD_HEIGHT, CARD_WIDTH } from "../dashboardCardDimensions"

import type { UserPaymentAccountResponse } from "./walletsApi"
import { fetchUserPaymentAccount, UserPaymentAccountQuery } from "./walletsApi"

const useStyles = makeStyles()((theme) => ({
    card: {
        flex: "0 0 auto",
        width: CARD_WIDTH,
        height: CARD_HEIGHT,
        borderRadius: ".75rem",
        padding: ".5rem 1rem",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "end",
        textAlign: "left",
        background: theme.palette.tailwind.blue[600],
        color: theme.palette.common.white,
        transition: theme.transitions.create(["background"]),
        "&:hover": {
            background: theme.palette.tailwind.blue[700],
        },
    },
    skeleton: {
        flex: "0 0 auto",
        width: CARD_WIDTH,
        height: CARD_HEIGHT,
        borderRadius: ".75rem",
    },
}))

const selectBalance = (data: UserPaymentAccountResponse) => data.userAccountBalance

export function WalletSummaryCards() {
    const { classes } = useStyles()
    const moneyFactory = useMoney({})
    const navigate = useNavigate()

    const { data: balance, isLoading } = useApiQuery(fetchUserPaymentAccount, {
        queryName: UserPaymentAccountQuery.USER_PAYMENT_ACCOUNT,
        dependencies: {},
        select: selectBalance,
        refetchInterval: RefetchIntervals.short,
    })

    if (isLoading) {
        return <Skeleton variant="rectangular" className={classes.skeleton} />
    }

    if (!balance) {
        return null
    }

    return (
        <ErrorBoundary fallback={<UserDashboardErrorFallbackCard />}>
            <ButtonBase className={classes.card} onClick={() => navigate(USER_REPORTS_SETTINGS_WALLETS_ROUTE)}>
                <Typography variant="h5" mb=".375rem">
                    {moneyFactory.format(toDinero(balance))}
                </Typography>
                <LocalizedStrict id="user-home-wallet-my-wallet">
                    <Typography variant="body1">My wallet</Typography>
                </LocalizedStrict>
            </ButtonBase>
        </ErrorBoundary>
    )
}
